// Professional CSS generation for drawer widget
// This is server-side only - generates CSS based on configuration

export function generateDrawerCSS(drawerConfig) {
  const {
    drawerWidth = 250,
    drawerHeight = 300,
    tabColor = '#064056',
    displayOn = 'all'
  } = drawerConfig;

  // Calculate responsive padding based on width
  // For widths below 250px, reduce padding to accommodate 128x128 QR better
  const numericWidth = parseInt(drawerWidth);
  let paddingTop = '15px';
  let paddingBottom = '15px';
  let paddingHorizontal = '15px';
  
  if (numericWidth < 250) {
    // At 200px width with 128px QR, reduce padding significantly
    paddingHorizontal = '8px';
    paddingTop = '10px';
    paddingBottom = '10px';
  }

  return `
/* PP Drawer Widget - CSS Reset and Isolation */
.pp-drawer, .pp-drawer *, .pp-drawer-tab, .pp-drawer-tab *, .pp-tab-logo, .pp-tab-text {
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
  font-size: 14px !important;
  font-weight: normal !important;
  font-style: normal !important;
  line-height: 1.4 !important;
  color: inherit !important;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  text-decoration: none !important;
  text-transform: none !important;
  text-shadow: none !important;
  letter-spacing: normal !important;
  word-spacing: normal !important;
  text-align: inherit !important;
  vertical-align: baseline !important;
  list-style: none !important;
  quotes: none !important;
  opacity: 1;
  visibility: visible;
  overflow: visible !important;
  clip: auto !important;
  zoom: 1 !important;
  float: none !important;
  clear: none !important;
  table-layout: auto !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  caption-side: top !important;
  empty-cells: show !important;
  white-space: normal !important;
  word-wrap: normal !important;
  word-break: normal !important;
  hyphens: none !important;
  user-select: auto !important;
  resize: none !important;
  cursor: auto !important;
  transition: none;
  transform: none !important;
  animation: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  mix-blend-mode: normal !important;
  isolation: auto !important;
}

/* The drawer itself */
.pp-drawer {
  position: fixed !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  right: -${parseInt(drawerWidth) * 2}px;
  width: ${drawerWidth}px !important;
  height: ${drawerHeight}px !important;
  min-height: 300px !important;
  background: #f9f9f9 !important;
  box-shadow: -2px 0 5px rgba(0,0,0,0.2) !important;
  transition: right 0.3s ease !important;
  padding: ${paddingTop} ${paddingHorizontal} ${paddingBottom} ${paddingHorizontal} !important;
  padding-left: var(--pp-tab-gap, ${paddingHorizontal}) !important;
  padding-right: var(--pp-tab-gap, ${paddingHorizontal}) !important;
  box-sizing: border-box !important;
  border-radius: 8px 0 0 8px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  z-index: 2147483646 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  color: #333 !important;
}

.pp-drawer.open {
  right: 0 !important;
  transform: translateY(-50%) !important;
}

/* The vertical tab/handle */
.pp-drawer-tab {
  position: fixed !important;
  top: 50%;
  right: 0;
  transform: translateY(-50%) !important;
  background: ${tabColor} !important;
  color: #fff !important;
  padding: 10px 5px !important;
  cursor: pointer !important;
  writing-mode: vertical-rl !important;
  text-align: center !important;
  font-weight: bold !important;
  border-radius: 6px 0 0 6px !important;
  z-index: 2147483647 !important;
  box-sizing: border-box !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: normal !important;
  text-transform: none !important;
  text-decoration: none !important;
  border: none !important;
  outline: none !important;
  margin: 0 !important;
  transition: right 0.3s ease !important;
}

.pp-drawer-tab:hover {
  opacity: 0.9 !important;
}

/* Attention-grabbing jiggle animation */
.pp-drawer-tab.pp-jiggle {
  animation: pp-jiggle 0.6s ease-in-out !important;
}

@keyframes pp-jiggle {
  0%, 100% { top: 50%; }
  25% { top: calc(50% - 8px); }
  50% { top: 50%; }
  75% { top: calc(50% - 8px); }
}

/* Peak animation - drawer slides out 50% and back */
.pp-drawer.pp-peak {
  animation: pp-peak 1s ease-in-out !important;
}

.pp-drawer-tab.pp-peak {
  animation: pp-peak-hide 1s ease-in-out !important;
}

@keyframes pp-peak {
  0% {
    right: -${parseInt(drawerWidth) * 2}px;
    visibility: visible;
  }
  50% {
    right: -${parseInt(drawerWidth) * 0.5}px;
    visibility: visible;
  }
  100% {
    right: -${parseInt(drawerWidth) * 2}px;
    visibility: hidden;
  }
}

@keyframes pp-peak-hide {
  0%, 100% {
    right: 0;
  }
  50% {
    right: -100px;
  }
}

/* Hide tab when drawer is open */
.pp-drawer.open + .pp-drawer-tab {
  right: -100px !important;
}

/* Tab logo styling */
.pp-tab-logo {
  width: 40px !important;
  height: 26px !important;
  margin: 0 2px 5px 0 !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 2px !important;
  background: transparent !important;
  object-fit: contain !important;
  display: inline-block !important;
  vertical-align: middle !important;
  flex-shrink: 0 !important;
  cursor: pointer !important;
}

/* Tab text styling */
.pp-tab-text {
  display: inline !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  color: inherit !important;
  line-height: inherit !important;
  text-align: inherit !important;
  writing-mode: inherit !important;
  letter-spacing: inherit !important;
  text-transform: inherit !important;
  text-decoration: inherit !important;
  border: none !important;
  background: transparent !important;
  vertical-align: middle !important;
  cursor: pointer !important;
}

/* QR card wrapper creates a consistent framed look around the QR */
.pp-qr-card {
  width: auto !important;
  max-width: 100% !important;
  margin: 0 auto !important;
  padding: 3px !important;
  border-radius: 0;
  border: none !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* QR render surface styling */
#ppQrContainer canvas,
#ppQrContainer svg,
#ppQrContainer img {
  max-width: 100% !important;
  height: auto !important;
  border-radius: 2px !important;
  display: block !important;
  margin: 0 auto !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  object-fit: contain !important;
}

/* QR container - keeps renderer centered and responsive inside the card */
#ppQrContainer {
  max-width: 180px !important;
  width: auto !important;
  margin: 0 auto !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Close button */
.pp-drawer-close {
  position: absolute !important;
  top: 10px !important;
  right: 10px !important;
  width: 24px !important;
  height: 24px !important;
  background: rgba(0, 0, 0, 0.1) !important;
  border: none !important;
  border-radius: 50% !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 16px !important;
  color: #666 !important;
  z-index: 10 !important;
  transition: background-color 0.2s ease !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
  line-height: 1 !important;
  padding: 0 !important;
  margin: 0 !important;
  outline: none !important;
}

.pp-drawer-close:hover {
  background: rgba(0, 0, 0, 0.2) !important;
  color: #333 !important;
}

.pp-drawer h3 {
  margin: 0 0 15px 0 !important;
  padding: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #333 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
  line-height: 1.3 !important;
  text-align: center !important;
  text-transform: none !important;
  text-decoration: none !important;
  letter-spacing: normal !important;
  word-spacing: normal !important;
  text-shadow: none !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  display: block !important;
}

/* Loading state */
.pp-qr-loading {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  color: #666 !important;
  font-size: 14px !important;
}

.pp-qr-spinner {
  width: 40px !important;
  height: 40px !important;
  border: 3px solid #f3f3f3 !important;
  border-top: 3px solid ${tabColor} !important;
  border-radius: 50% !important;
  animation: pp-spin 1s linear infinite !important;
  margin-bottom: 10px !important;
}

@keyframes pp-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state */
.pp-qr-error {
  color: #d32f2f !important;
  font-size: 14px !important;
  text-align: center !important;
  padding: 20px !important;
}

/* Responsive display controls */
${generateResponsiveCSS(displayOn)}
`.trim();
}

function generateResponsiveCSS(displayOn) {
  switch (displayOn) {
    case 'mobile-only':
      return `
@media (min-width: 768px) {
  .pp-drawer, .pp-drawer-tab { display: none !important; }
}`;
    case 'tablet-up':
      return `
@media (max-width: 767px) {
  .pp-drawer, .pp-drawer-tab { display: none !important; }
}`;
    case 'mobile':
      return `
@media (min-width: 768px) {
  .pp-drawer, .pp-drawer-tab { display: none !important; }
}`;
    case 'tablet':
      return `
@media (max-width: 767px), (min-width: 1024px) {
  .pp-drawer, .pp-drawer-tab { display: none !important; }
}`;
    case 'desktop':
      return `
@media (max-width: 1023px) {
  .pp-drawer, .pp-drawer-tab { display: none !important; }
}`;
    case 'all':
    default:
      return '/* Display on all devices */';
  }
}
